import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import './SimpleEditorField.css';

const ToolbarButton = ({ onClick, active, label, title }) => (
    <button
        type="button"
        className={`simple-editor-btn ${active ? 'active' : ''}`}
        onClick={onClick}
        title={title || label}
    >
        {label}
    </button>
);

const normalizeContent = (value) => {
    if (!value || value === '<p></p>') return '';
    return value;
};

const SimpleEditorField = ({
    value,
    onChange,
    placeholder = 'Write here...',
    minHeight = 180,
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                autolink: true,
                openOnClick: false,
                defaultProtocol: 'https',
            }),
            Highlight,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content: normalizeContent(value),
        editorProps: {
            attributes: {
                class: 'simple-editor-content',
            },
        },
        onUpdate: ({ editor: current }) => {
            const html = normalizeContent(current.getHTML());
            if (html !== (normalizeContent(value))) {
                onChange?.(html);
            }
        },
    });

    useEffect(() => {
        if (!editor) return;
        const current = normalizeContent(editor.getHTML());
        const incoming = normalizeContent(value);
        if (current !== incoming) {
            editor.commands.setContent(incoming || '<p></p>', false);
        }
    }, [editor, value]);

    if (!editor) {
        return <div className="simple-editor-loading">Loading editor...</div>;
    }

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href || '';
        const url = window.prompt('Enter URL', previousUrl);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <div className="simple-editor-root" style={{ '--simple-editor-min-height': `${minHeight}px` }}>
            <div className="simple-editor-toolbar">
                <ToolbarButton
                    label="B"
                    title="Bold"
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                />
                <ToolbarButton
                    label="I"
                    title="Italic"
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                />
                <ToolbarButton
                    label="U"
                    title="Underline"
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                />
                <ToolbarButton
                    label="H"
                    title="Highlight"
                    active={editor.isActive('highlight')}
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                />
                <ToolbarButton
                    label="H1"
                    title="Heading 1"
                    active={editor.isActive('heading', { level: 1 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                />
                <ToolbarButton
                    label="H2"
                    title="Heading 2"
                    active={editor.isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                />
                <ToolbarButton
                    label="• List"
                    title="Bullet List"
                    active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                />
                <ToolbarButton
                    label="1. List"
                    title="Ordered List"
                    active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                />
                <ToolbarButton
                    label="↔"
                    title="Add/Edit Link"
                    active={editor.isActive('link')}
                    onClick={setLink}
                />
                <ToolbarButton
                    label="↶"
                    title="Undo"
                    active={false}
                    onClick={() => editor.chain().focus().undo().run()}
                />
                <ToolbarButton
                    label="↷"
                    title="Redo"
                    active={false}
                    onClick={() => editor.chain().focus().redo().run()}
                />
            </div>

            <div className="simple-editor-surface" data-placeholder={placeholder}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

export default SimpleEditorField;
